/**
 * Format currency with support for large numbers (M, B, T abbreviations)
 * @param amount - The amount to format
 * @param options - Formatting options
 * @returns Formatted currency string
 */
export const formatCurrency = (
	amount: number | string | null | undefined,
	options?: {
		showAbbreviation?: boolean; // Show M, B, T for large numbers
		minimumFractionDigits?: number;
		maximumFractionDigits?: number;
		currency?: string;
	},
): string => {
	// Handle invalid values
	const validAmount =
		amount === null || amount === undefined || amount === ""
			? 0
			: typeof amount === "string"
			? parseFloat(amount) || 0
			: isNaN(amount)
			? 0
			: amount;

	const {
		showAbbreviation = true,
		minimumFractionDigits = 0,
		maximumFractionDigits = 0,
		currency = "NGN",
	} = options || {};

	// If showAbbreviation is false, use standard formatting
	if (!showAbbreviation) {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency,
			minimumFractionDigits,
			maximumFractionDigits,
		}).format(validAmount);
	}

	// Format with abbreviations for large numbers
	const absAmount = Math.abs(validAmount);
	const sign = validAmount < 0 ? "-" : "";

	let formattedValue: string;
	let suffix = "";

	if (absAmount >= 1_000_000_000_000) {
		// Trillions
		const trillions = absAmount / 1_000_000_000_000;
		formattedValue = trillions.toFixed(1).replace(/\.0$/, "");
		suffix = "T";
	} else if (absAmount >= 1_000_000_000) {
		// Billions
		const billions = absAmount / 1_000_000_000;
		formattedValue = billions.toFixed(1).replace(/\.0$/, "");
		suffix = "B";
	} else if (absAmount >= 1_000_000) {
		// Millions
		const millions = absAmount / 1_000_000;
		formattedValue = millions.toFixed(1).replace(/\.0$/, "");
		suffix = "M";
	} else if (absAmount >= 1_000) {
		// Thousands - show as K for consistency, but only if > 10K
		if (absAmount >= 10_000) {
			const thousands = absAmount / 1_000;
			formattedValue = thousands.toFixed(1).replace(/\.0$/, "");
			suffix = "K";
		} else {
			// For amounts less than 10K, use standard formatting
			return new Intl.NumberFormat("en-NG", {
				style: "currency",
				currency,
				minimumFractionDigits,
				maximumFractionDigits,
			}).format(validAmount);
		}
	} else {
		// For amounts less than 1K, use standard formatting
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency,
			minimumFractionDigits,
			maximumFractionDigits,
		}).format(validAmount);
	}

	// Get currency symbol
	const currencySymbol =
		new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency,
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		})
			.formatToParts(0)
			.find((part) => part.type === "currency")?.value || "₦";

	return `${sign}${currencySymbol}${formattedValue}${suffix}`;
};
